import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, MessageCircle, Instagram } from "lucide-react";
import { buildWhatsAppLink, COMPANY } from "@/lib/constants";
import Logo from "@/components/Logo";

const navLinks = [
  { href: "#spar-check", label: "Spar-Check" },
  { href: "#konfigurator", label: "Konfigurator" },
  { href: "#leistungen", label: "Leistungen" },
  { href: "#ablauf", label: "Ablauf" },
  { href: "#faq", label: "FAQ" },
  { href: "#kontakt", label: "Kontakt" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-28 sm:h-32">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <Logo variant="navbar" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.replace("#", "")}`}
                className="link-underline text-sm font-medium text-slate-700 hover:text-[#1FA3B5] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={COMPANY.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-instagram-button"
              aria-label="Instagram"
              className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-slate-200 text-slate-700 hover:text-white hover:bg-gradient-to-br hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] hover:border-transparent transition-all"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-whatsapp-button"
              className="inline-flex items-center gap-2 bg-[#1FA3B5] text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#178A9A] hover:-translate-y-0.5 transition-all shadow-lg shadow-[#1FA3B5]/25"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Beratung
            </a>
          </div>

          <button
            type="button"
            onClick={() => setOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-slate-900"
            aria-label="Menü öffnen"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-6 border-t border-slate-200/70 pt-4 -mx-6 px-6 bg-white/95 backdrop-blur-xl rounded-b-2xl shadow-lg" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-slate-800 hover:text-[#1FA3B5]"
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-whatsapp-button"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#1FA3B5] text-white rounded-full px-6 py-3 text-sm font-semibold"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp Beratung
              </a>
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-instagram-button"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white rounded-full px-6 py-3 text-sm font-semibold"
              >
                <Instagram className="w-4 h-4" /> Folge uns auf Instagram
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
